import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useApp } from '../lib/app-context';
import {
  ALL_AMENITIES,
  AMENITY_LABEL,
  MODE_LABEL,
  STATUS_LABEL,
  addDays,
  addMonths,
  mad,
  monthOf,
  today,
} from '../lib/format';
import { Badge, Field, Notice, Select, Spinner, TextInput, Toggle } from '../components/ui';
import type {
  NightStatus,
  Photo,
  PropertyDetail,
  PublishRequirement,
  RentalModeName,
} from '../lib/types';
import { t } from '../lib/i18n';
import { translateServerText } from '../lib/server-text';

/**
 * One listing, from the host's side: edit, price, photos, calendar, publish.
 *
 * The calendar is per unit, because that is how availability actually works — a
 * property with two rentable rooms has three calendars (the whole place and each
 * room) and blocking the whole place blocks the rooms with it.
 */

type EditResponse = { property: PropertyDetail; publishRequirements: PublishRequirement[] };

export default function HostListing() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast, reportError } = useApp();

  const [data, setData] = useState<EditResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const location = useLocation();
  // "Bloquer des dates" on the dashboard lands straight on the calendar.
  const [tab, setTab] = useState<'details' | 'prices' | 'photos' | 'calendar'>(
    location.hash === '#calendrier' ? 'calendar' : 'details',
  );
  const [units, setUnits] = useState<
    { id: string; kind: string; label: string; roomId: string | null }[]
  >([]);
  const [selectedUnit, setSelectedUnit] = useState('');
  const [nights, setNights] = useState<NightStatus[]>([]);
  const [monthStart, setMonthStart] = useState(() => `${today().slice(0, 7)}-01`);
  const [pendingRange, setPendingRange] = useState<{ start: string; end: string } | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const response = await api.get<EditResponse>(`/properties/${id}/edit`);
      setData(response);
      const unitResponse = await api.get<{ units: typeof units }>(`/properties/${id}/units`);
      setUnits(unitResponse.units);
      setSelectedUnit((current) => current || (unitResponse.units[0]?.id ?? ''));
    } catch (error) {
      reportError(error, t("Annonce introuvable"));
    }
  }, [id, reportError]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadCalendar = useCallback(async () => {
    if (!id || !selectedUnit) return;
    const from = monthStart;
    const to = addMonths(monthStart, 2);
    try {
      const response = await api.get<{ nights: NightStatus[] }>(
        `/properties/${id}/availability?unitId=${selectedUnit}&from=${from}&to=${to}`,
      );
      setNights(response.nights);
    } catch (error) {
      reportError(error);
    }
  }, [id, selectedUnit, monthStart, reportError]);

  useEffect(() => {
    if (tab === 'calendar') void loadCalendar();
  }, [tab, loadCalendar]);

  const patch = async (payload: Record<string, unknown>) => {
    if (!id) return;
    setBusy(true);
    try {
      await api.patch(`/properties/${id}`, payload);
      await load();
      toast(t("Annonce mise à jour."), 'ok');
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const monthDays = useMemo(() => {
    const [year, month] = monthStart.split('-').map(Number);
    const first = new Date(Date.UTC(year ?? 2026, (month ?? 1) - 1, 1));
    const daysInMonth = new Date(Date.UTC(year ?? 2026, month ?? 1, 0)).getUTCDate();
    // Monday-first grid, which is the Moroccan convention.
    const leading = (first.getUTCDay() + 6) % 7;
    return { daysInMonth, leading };
  }, [monthStart]);

  if (!data) {
    return (
      <div className="page center" style={{ paddingTop: 60 }}>
        <Spinner label={t("Chargement…")} />
      </div>
    );
  }

  const { property, publishRequirements } = data;
  const missing = publishRequirements.filter((requirement) => !requirement.met);
  const nightByDate = new Map(nights.map((night) => [night.date, night]));

  const blockRange = async (start: string, end: string, remove: boolean) => {
    if (!id) return;
    setBusy(true);
    try {
      const payload = { startDate: start, endDate: end, unitId: selectedUnit, reason: 'MANUAL' };
      if (remove) {
        const response = await api.delete<{ released: number }>(
          `/properties/${id}/availability/block`,
          payload,
        );
        toast(t('{count} nuit(s) libérée(s).', { count: response.released }), 'ok');
      } else {
        const response = await api.post<{ blocked: number; alreadyTaken: string[] }>(
          `/properties/${id}/availability/block`,
          payload,
        );
        toast(
          response.alreadyTaken.length > 0
            ? t('{count} nuit(s) bloquée(s), {taken} déjà prise(s).', { count: response.blocked, taken: response.alreadyTaken.length })
            : t('{count} nuit(s) bloquée(s).', { count: response.blocked }),
          'ok',
        );
      }
      setPendingRange(null);
      await loadCalendar();
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page page--mid stack stack--loose">
      <div className="stack stack--tight">
        <Link to="/hote" className="small">
          {t("← Tableau de bord")}
        </Link>
        <div className="row row--between row--wrap" style={{ gap: 10 }}>
          <h1 style={{ fontSize: 24 }}>{property.title}</h1>
          <div className="row" style={{ gap: 8 }}>
            <Badge tone={property.status === 'ACTIVE' ? 'badge--ok' : 'badge--warn'}>
              {STATUS_LABEL[property.status]}
            </Badge>
            {property.visited ? <Badge tone="badge--visited">{t("Visité")}</Badge> : null}
          </div>
        </div>
        <p className="muted small" style={{ margin: 0 }}>
          {property.neighborhood}, {t(property.city)}
          {property.addressPrecise ? ` · ${property.addressPrecise}` : ''}
        </p>
      </div>

      {missing.length > 0 ? (
        <Notice tone="warn">
          <div className="stack stack--tight">
            <span className="strong">{t("Avant de publier :")}</span>
            {missing.map((requirement) => (
              <span key={requirement.key}>• {translateServerText(requirement.message)}</span>
            ))}
          </div>
        </Notice>
      ) : property.status === 'DRAFT' ? (
        <div className="card card--pad row row--between row--wrap" style={{ gap: 12 }}>
          <span className="small">{t("Tout est prêt. L’annonce passera en revue avant publication.")}</span>
          <button
            className="btn"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.post(`/properties/${property.id}/submit`);
                toast(t("Envoyée pour vérification. Visite sous 48 h."), 'ok');
                await load();
              } catch (error) {
                reportError(error);
              } finally {
                setBusy(false);
              }
            }}
          >
            {t("Envoyer pour vérification")}
          </button>
        </div>
      ) : null}

      <div className="tabs" style={{ border: '1px solid var(--line)', borderRadius: 12, overflow: 'hidden' }}>
        {(
          [
            ['details', t("Détails")],
            ['prices', t("Prix & chambres")],
            ['photos', t("Photos")],
            ['calendar', t("Calendrier")],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={`tab ${tab === value ? 'tab--on' : ''}`}
            onClick={() => setTab(value)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'details' ? (
        <DetailsTab property={property} busy={busy} onSave={patch} />
      ) : null}

      {tab === 'prices' ? (
        <PricesTab property={property} onChanged={load} />
      ) : null}

      {tab === 'photos' ? (
        <PhotosTab property={property} onChanged={load} />
      ) : null}

      {tab === 'calendar' ? (
        <div className="card card--pad stack">
          <div className="row row--wrap" style={{ gap: 10 }}>
            <div style={{ flex: '1 1 220px' }}>
              <Field label={t("Unité")}>
                <Select
                  value={selectedUnit}
                  onChange={setSelectedUnit}
                  options={units.map((unit) => ({
                    value: unit.id,
                    label:
                      unit.kind === 'WHOLE_PROPERTY'
                        ? t("Logement entier")
                        : `${unit.label} (${unit.kind === 'BED' ? t('lit') : t('chambre')})`,
                  }))}
                />
              </Field>
            </div>
            <div className="row" style={{ gap: 8, alignSelf: 'flex-end' }}>
              <button
                className="btn btn--ghost btn--sm"
                onClick={() => setMonthStart((current) => addMonths(current, -1))}
              >
                ←
              </button>
              <span className="strong" style={{ minWidth: 130, textAlign: 'center' }}>
                {monthOf(monthStart)}
              </span>
              <button
                className="btn btn--ghost btn--sm"
                onClick={() => setMonthStart((current) => addMonths(current, 1))}
              >
                →
              </button>
            </div>
          </div>

          <div className="calendar">
            {[t("Lun"), t("Mar"), t("Mer"), t("Jeu"), t("Ven"), t("Sam"), t("Dim")].map((label) => (
              <span key={label} className="calendar__head">
                {label}
              </span>
            ))}
            {Array.from({ length: monthDays.leading }, (_, index) => (
              <span key={`lead-${index}`} />
            ))}
            {Array.from({ length: monthDays.daysInMonth }, (_, index) => {
              const date = `${monthStart.slice(0, 8)}${String(index + 1).padStart(2, '0')}`;
              const night = nightByDate.get(date);
              const isBooked = night && !night.available && night.reason === 'BOOKING';
              const isBlocked = night && !night.available && night.reason !== 'BOOKING';
              const inPending =
                pendingRange &&
                date >= pendingRange.start &&
                date < (pendingRange.end || addDays(pendingRange.start, 1));
              return (
                <button
                  key={date}
                  type="button"
                  className={`calendar__day ${isBooked ? 'calendar__day--taken' : ''} ${
                    isBlocked ? 'calendar__day--blocked' : ''
                  } ${inPending ? 'calendar__day--range' : ''}`}
                  disabled={Boolean(isBooked)}
                  title={isBooked ? t("Réservé") : isBlocked ? t("Bloqué") : t("Disponible")}
                  onClick={() => {
                    if (!pendingRange || pendingRange.end) {
                      setPendingRange({ start: date, end: '' });
                    } else {
                      const end = date > pendingRange.start ? addDays(date, 1) : addDays(pendingRange.start, 1);
                      const start = date > pendingRange.start ? pendingRange.start : date;
                      setPendingRange({ start, end });
                    }
                  }}
                >
                  {index + 1}
                </button>
              );
            })}
          </div>

          <div className="row row--wrap" style={{ gap: 12 }}>
            <span className="tiny row" style={{ gap: 6 }}>
              <span className="calendar__day calendar__day--taken" style={{ width: 16, height: 16 }} /> {t("Réservé")}
            </span>
            <span className="tiny row" style={{ gap: 6 }}>
              <span className="calendar__day calendar__day--blocked" style={{ width: 16, height: 16 }} /> {t("Bloqué")}
            </span>
          </div>

          {pendingRange?.end ? (
            <div className="card card--pad row row--between row--wrap" style={{ gap: 10 }}>
              <span className="small">
                {t("Du")} {pendingRange.start} au {pendingRange.end} {t("(exclu)")}
              </span>
              <div className="row" style={{ gap: 8 }}>
                <button
                  className="btn btn--sm"
                  disabled={busy}
                  onClick={() => blockRange(pendingRange.start, pendingRange.end, false)}
                >
                  {t("Bloquer")}
                </button>
                <button
                  className="btn btn--ghost btn--sm"
                  disabled={busy}
                  onClick={() => blockRange(pendingRange.start, pendingRange.end, true)}
                >
                  {t("Débloquer")}
                </button>
                <button className="btn btn--quiet btn--sm" onClick={() => setPendingRange(null)}>
                  {t("Annuler")}
                </button>
              </div>
            </div>
          ) : (
            <p className="tiny muted" style={{ margin: 0 }}>
              {t("Touchez une date de début puis une date de fin pour bloquer une période. Les nuits réservées ne peuvent pas être libérées depuis ici — ce serait survendre la chambre.")}
            </p>
          )}
        </div>
      ) : null}

      <div className="card card--pad row row--between row--wrap" style={{ gap: 12 }}>
        <div className="stack stack--tight">
          <span className="strong">{t("Statut de l’annonce")}</span>
          <span className="small muted">
            {t("Mettre en pause la retire des résultats sans supprimer vos données.")}
          </span>
        </div>
        <div className="row row--wrap" style={{ gap: 8 }}>
          {property.status === 'ACTIVE' ? (
            <button
              className="btn btn--ghost btn--sm"
              disabled={busy}
              onClick={async () => {
                await api.patch(`/properties/${property.id}/status`, { status: 'PAUSED' });
                toast(t("Annonce mise en pause."));
                await load();
              }}
            >
              {t("Mettre en pause")}
            </button>
          ) : null}
          {property.status === 'PAUSED' ? (
            <button
              className="btn btn--sm"
              disabled={busy}
              onClick={async () => {
                await api.post(`/properties/${property.id}/submit`);
                toast(t("Renvoyée en vérification."));
                await load();
              }}
            >
              {t("Remettre en ligne")}
            </button>
          ) : null}
          <button
            className="btn btn--danger btn--sm"
            disabled={busy}
            onClick={async () => {
              try {
                await api.delete(`/properties/${property.id}`);
                toast(t("Annonce supprimée."));
                navigate('/hote');
              } catch (error) {
                reportError(error);
              }
            }}
          >
            {t("Supprimer")}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailsTab({
  property,
  busy,
  onSave,
}: {
  property: PropertyDetail;
  busy: boolean;
  onSave: (payload: Record<string, unknown>) => Promise<void>;
}) {
  const [title, setTitle] = useState(property.title);
  const [description, setDescription] = useState(property.description);
  const [houseRules, setHouseRules] = useState(property.houseRules);
  const [amenities, setAmenities] = useState(property.amenities);
  const [furnished, setFurnished] = useState(property.furnished);
  const [address, setAddress] = useState(property.addressPrecise ?? '');

  return (
    <div className="card card--pad stack">
      <Field label={t("Titre")}>
        <TextInput value={title} onChange={setTitle} />
      </Field>
      <Field label={t("Description")}>
        <textarea
          className="textarea"
          rows={5}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </Field>
      <Field label={t("Adresse exacte")} hint={t("Communiquée au locataire une fois la réservation confirmée.")}>
        <TextInput value={address} onChange={setAddress} />
      </Field>
      <Field label={t("Règles de la maison")}>
        <textarea
          className="textarea"
          rows={3}
          value={houseRules}
          onChange={(event) => setHouseRules(event.target.value)}
        />
      </Field>
      <Toggle on={furnished} onChange={setFurnished} label={t("Meublé")} />
      <div className="stack stack--tight">
        <span className="field__label">{t("Équipements")}</span>
        <div className="row row--wrap" style={{ gap: 8 }}>
          {ALL_AMENITIES.map((amenity) => (
            <button
              key={amenity}
              type="button"
              className={`chip ${amenities.includes(amenity) ? 'chip--on' : ''}`}
              onClick={() =>
                setAmenities((current) =>
                  current.includes(amenity)
                    ? current.filter((item) => item !== amenity)
                    : [...current, amenity],
                )
              }
            >
              {AMENITY_LABEL[amenity]}
            </button>
          ))}
        </div>
      </div>
      <button
        className="btn"
        disabled={busy}
        onClick={() =>
          onSave({
            title,
            description,
            houseRules,
            amenities,
            furnished,
            ...(address ? { addressPrecise: address } : {}),
          })
        }
      >
        {busy ? t("Enregistrement…") : t("Enregistrer")}
      </button>
    </div>
  );
}

function PricesTab({ property, onChanged }: { property: PropertyDetail; onChanged: () => Promise<void> }) {
  const { toast, reportError } = useApp();
  const [busy, setBusy] = useState(false);
  const [roomName, setRoomName] = useState('');

  const setMode = async (mode: RentalModeName, priceDirhams: number, minStay: number, isActive: boolean) => {
    setBusy(true);
    try {
      await api.put(`/properties/${property.id}/rental-modes`, {
        mode,
        priceCentimes: priceDirhams * 100,
        minStay,
        isActive,
      });
      await onChanged();
      toast(t("Tarif enregistré."), 'ok');
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      {(['COLOCATION', 'MONTHLY', 'NIGHTLY'] as RentalModeName[]).map((mode) => {
        const existing = property.rentalModes.find((item) => item.mode === mode);
        return (
          <ModeEditor
            key={mode}
            mode={mode}
            price={existing ? existing.priceCentimes / 100 : mode === 'NIGHTLY' ? 350 : 2400}
            minStay={existing?.minStay ?? (mode === 'NIGHTLY' ? 1 : 3)}
            active={existing?.isActive ?? false}
            busy={busy}
            onSave={setMode}
          />
        );
      })}

      <div className="card card--pad stack stack--tight">
        <span className="strong">{t("Chambres")}</span>
        {property.rooms.length === 0 ? (
          <span className="small muted">
            {t("Aucune chambre séparée. Ajoutez-en une pour la louer indépendamment du logement entier.")}
          </span>
        ) : (
          property.rooms.map((room) => (
            <div key={room.id} className="row row--between">
              <span className="small">
                {room.name} · {room.beds} {t("lit(s)")}
                {room.rentalModes[0]
                  ? ` · ${mad(room.rentalModes[0].priceCentimes)}${
                      room.rentalModes[0].unit === 'night' ? '/nuit' : '/mois'
                    }`
                  : t(" · pas de prix")}
              </span>
              <button
                className="btn btn--quiet btn--sm"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await api.delete(`/rooms/${room.id}`);
                    await onChanged();
                    toast(t("Chambre supprimée."));
                  } catch (error) {
                    reportError(error);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {t("Supprimer")}
              </button>
            </div>
          ))
        )}
        <div className="row" style={{ gap: 8 }}>
          <TextInput value={roomName} onChange={setRoomName} placeholder={t("Nom de la chambre")} />
          <button
            className="btn btn--ghost btn--sm"
            disabled={busy || roomName.trim().length === 0}
            onClick={async () => {
              setBusy(true);
              try {
                await api.post(`/properties/${property.id}/rooms`, {
                  name: roomName,
                  beds: 1,
                  maxGuests: 1,
                  bathroomType: 'SHARED',
                });
                setRoomName('');
                await onChanged();
              } catch (error) {
                reportError(error);
              } finally {
                setBusy(false);
              }
            }}
          >
            {t("Ajouter")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModeEditor({
  mode,
  price,
  minStay,
  active,
  busy,
  onSave,
}: {
  mode: RentalModeName;
  price: number;
  minStay: number;
  active: boolean;
  busy: boolean;
  onSave: (mode: RentalModeName, price: number, minStay: number, isActive: boolean) => Promise<void>;
}) {
  const [draftPrice, setDraftPrice] = useState(String(price));
  const [draftMinStay, setDraftMinStay] = useState(minStay);
  const [enabled, setEnabled] = useState(active);

  return (
    <div className="card card--pad stack stack--tight">
      <Toggle on={enabled} onChange={setEnabled} label={MODE_LABEL[mode]} />
      <div className="row row--wrap" style={{ gap: 10 }}>
        <div style={{ flex: '1 1 130px' }}>
          <Field label={mode === 'NIGHTLY' ? t("DH / nuit") : t("DH / mois")}>
            <TextInput
              value={draftPrice}
              onChange={(value) => setDraftPrice(value.replace(/\D/g, ''))}
              inputMode="numeric"
            />
          </Field>
        </div>
        <div style={{ flex: '1 1 130px' }}>
          <Field label={mode === 'NIGHTLY' ? t("Nuits min.") : t("Mois min.")}>
            <Select
              value={String(draftMinStay)}
              onChange={(value) => setDraftMinStay(Number(value))}
              options={(mode === 'NIGHTLY' ? [1, 2, 3, 7] : [1, 3, 6, 9, 10, 12]).map((count) => ({
                value: String(count),
                label: String(count),
              }))}
            />
          </Field>
        </div>
        <button
          className="btn btn--ghost btn--sm"
          style={{ alignSelf: 'flex-end' }}
          disabled={busy || draftPrice === ''}
          onClick={() => onSave(mode, Number(draftPrice), draftMinStay, enabled)}
        >
          {t("Enregistrer")}
        </button>
      </div>
    </div>
  );
}

function PhotosTab({ property, onChanged }: { property: PropertyDetail; onChanged: () => Promise<void> }) {
  const { toast, reportError } = useApp();
  const [busy, setBusy] = useState(false);

  const upload = async (file: File) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await api.upload<{ photo: Photo }>(`/properties/${property.id}/photos`, form);
      await onChanged();
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card card--pad stack">
      <label className="btn btn--ghost btn--block" style={{ cursor: 'pointer' }}>
        {busy ? t("Envoi…") : t("+ Ajouter une photo")}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/avif"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void upload(file);
            event.target.value = '';
          }}
        />
      </label>
      <div className="grid grid--tiles">
        {property.photos.map((photo) => (
          <div key={photo.id} className="card stack stack--tight" style={{ padding: 0 }}>
            <img
              src={photo.url}
              alt=""
              style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }}
            />
            <div className="row row--between" style={{ padding: '6px 8px' }}>
              {photo.isCover ? (
                <span className="tiny strong" style={{ color: 'var(--saff-ink)' }}>
                  {t("★ Couverture")}
                </span>
              ) : (
                <button
                  className="btn btn--quiet btn--sm"
                  disabled={busy}
                  onClick={async () => {
                    await api.post(`/photos/${photo.id}/cover`);
                    await onChanged();
                    toast(t("Photo de couverture mise à jour."), 'ok');
                  }}
                >
                  {t("Couverture")}
                </button>
              )}
              <button
                className="btn btn--quiet btn--sm"
                disabled={busy}
                onClick={async () => {
                  await api.delete(`/photos/${photo.id}`);
                  await onChanged();
                }}
              >
                {t("Supprimer")}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
